import { ConvertedFile } from '../models/converted-file';
import { createReducer, on } from '@ngrx/store';
import * as HomeActions from './home.actions';
import {
  loadConvertedFilesSuccess,
  moveFileToEndOfGerberQueue,
  moveFileToEndOfLectraQueue,
  moveFileToEndOfQueue,
} from './home.actions';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { UploadedFile } from '../models/uploaded-file';
import { JobStatus } from '../models/job-status';

export interface HomeState {
  uploadedFiles: UploadedFile[];
  gerberFiles: UploadedFile[];
  lectraFiles: UploadedFile[];
  convertedFiles: ConvertedFile[];
  monthlyConvertedFiles: { count: number };
  loading: boolean;
  fileBoxLocked: boolean;
  gerberLocked: boolean;
  lectraLocked: boolean;
}

export const initialState: HomeState = {
  uploadedFiles: [],
  gerberFiles: [],
  lectraFiles: [],
  convertedFiles: [],
  monthlyConvertedFiles: { count: 0 },
  loading: false,
  fileBoxLocked: false,
  gerberLocked: false,
  lectraLocked: false,
};

export const homeReducer = createReducer(
  initialState,
  on(HomeActions.loadGerberFiles, (state) => ({
    ...state,
    loading: true,
  })),
  on(HomeActions.loadGerberFilesSuccess, (state, { gerberFiles }) => ({
    ...state,
    gerberFiles: gerberFiles,
    loading: false,
  })),
  on(HomeActions.loadLectraFiles, (state) => ({
    ...state,
    loading: true,
  })),
  on(HomeActions.loadLectraFilesSuccess, (state, { lectraFiles }) => ({
    ...state,
    lectraFiles: lectraFiles,
    loading: false,
  })),
  on(HomeActions.loadConvertedFiles, (state) => ({
    ...state,
    loading: true,
  })),
  on(loadConvertedFilesSuccess, (state, { files }) => ({
    ...state,
    convertedFiles: files,
    loading: false,
  })),
  on(HomeActions.addGerberFile, (state, { gerberFile }) => {
    const updatedFiles = [
      { ...gerberFile, index: 0 },
      ...state.gerberFiles.map((f, i) => ({ ...f, index: i + 1 })),
    ];
    return {
      ...state,
      gerberFiles: updatedFiles,
      loading: false,
    };
  }),
  on(HomeActions.addLectraFile, (state, { lectraFile }) => {
    const updatedFiles = [
      { ...lectraFile, index: 0 },
      ...state.lectraFiles.map((f, i) => ({ ...f, index: i + 1 })),
    ];
    return {
      ...state,
      lectraFiles: updatedFiles,
      loading: false,
    };
  }),
  on(HomeActions.addConvertedFile, (state, { file }) => ({
    ...state,
    convertedFiles: [...state.convertedFiles, file],
  })),
  on(moveFileToEndOfQueue, (state, { fileId }) => {
    const files = [...state.uploadedFiles];
    const index = files.findIndex((f) => f.fileId === fileId);
    if (index === -1) return state;

    const [file] = files.splice(index, 1);
    files.push(file);

    return {
      ...state,
      uploadedFiles: files,
    };
  }),
  on(moveFileToEndOfGerberQueue, (state, { fileId }) => {
    const files = [...state.gerberFiles];
    const index = files.findIndex((f) => f.fileId === fileId);
    if (index === -1) return state;

    const [file] = files.splice(index, 1);
    files.push(file);

    return {
      ...state,
      gerberFiles: files,
    };
  }),
  on(moveFileToEndOfLectraQueue, (state, { fileId }) => {
    const files = [...state.lectraFiles];
    const index = files.findIndex((f) => f.fileId === fileId);
    if (index === -1) return state;

    const [file] = files.splice(index, 1);
    files.push(file);

    return {
      ...state,
      lectraFiles: files,
    };
  }),
  on(HomeActions.updateUploadedFileStatus, (state, { fileId, status }) => ({
    ...state,
    uploadedFiles: state.uploadedFiles.map((file) =>
      file.fileId === fileId ? { ...file, status } : file,
    ),
  })),
  on(HomeActions.updateGerberFileStatus, (state, { fileId, status }) => ({
    ...state,
    gerberFiles: state.gerberFiles.map((file) =>
      file.fileId === fileId ? { ...file, status } : file,
    ),
  })),
  on(HomeActions.updateLectraFileStatus, (state, { fileId, status }) => ({
    ...state,
    lectraFiles: state.lectraFiles.map((file) =>
      file.fileId === fileId ? { ...file, status } : file,
    ),
  })),
  on(
    HomeActions.reorderUploadedFiles,
    (state, { previousIndex, currentIndex }) => {
      const updatedFiles = [...state.uploadedFiles];
      moveItemInArray(updatedFiles, previousIndex, currentIndex);
      const reindexFiles = updatedFiles.map((file, index) => ({
        ...file,
        index: index,
      }));

      return { ...state, uploadedFiles: reindexFiles };
    },
  ),
  on(
    HomeActions.reorderGerberFiles,
    (state, { previousIndex, currentIndex }) => {
      const updatedFiles = [...state.gerberFiles];
      moveItemInArray(updatedFiles, previousIndex, currentIndex);
      const reindexFiles = updatedFiles.map((file, index) => ({
        ...file,
        index: index,
      }));

      return { ...state, gerberFiles: reindexFiles };
    },
  ),
  on(
    HomeActions.reorderLectraFiles,
    (state, { previousIndex, currentIndex }) => {
      const updatedFiles = [...state.lectraFiles];
      moveItemInArray(updatedFiles, previousIndex, currentIndex);
      const reindexFiles = updatedFiles.map((file, index) => ({
        ...file,
        index: index,
      }));

      return { ...state, lectraFiles: reindexFiles };
    },
  ),
  on(HomeActions.resetFileStatus, (state) => {
    const updatedFiles = state.uploadedFiles.map((file) => ({
      ...file,
      status: JobStatus.Wait,
    }));
    return { ...state, uploadedFiles: updatedFiles };
  }),
  on(HomeActions.resetGerberFileStatus, (state) => {
    const updatedFiles = state.gerberFiles.map((file) => ({
      ...file,
      status: JobStatus.Wait,
    }));
    return { ...state, gerberFiles: updatedFiles };
  }),
  on(HomeActions.resetLectraFileStatus, (state) => {
    const updatedFiles = state.lectraFiles.map((file) => ({
      ...file,
      status: JobStatus.Wait,
    }));
    return { ...state, lectraFiles: updatedFiles };
  }),
  on(HomeActions.deleteUploadedFile, (state, { fileId }) => {
    return {
      ...state,
      uploadedFiles: state.uploadedFiles.filter(
        (file) => file.fileId !== fileId,
      ),
    };
  }),
  on(HomeActions.deleteGerberFile, (state, { fileId }) => {
    return {
      ...state,
      gerberFiles: state.gerberFiles.filter((file) => file.fileId !== fileId),
    };
  }),
  on(HomeActions.deleteLectraFile, (state, { fileId }) => {
    return {
      ...state,
      lectraFiles: state.lectraFiles.filter((file) => file.fileId !== fileId),
    };
  }),
  on(HomeActions.clearHomeState, () => initialState),
  on(HomeActions.clearFileQueue, (state) => ({
    ...state,
    uploadedFiles: [],
  })),
  on(HomeActions.clearGerberFileQueue, (state) => ({
    ...state,
    gerberFiles: [],
  })),
  on(HomeActions.clearLectraFileQueue, (state) => ({
    ...state,
    lectraFiles: [],
  })),
  on(HomeActions.clearConvertedFiles, (state) => ({
    ...state,
    convertedFiles: [],
  })),
  on(HomeActions.deleteConvertedFile, (state, { fileId }) => {
    return {
      ...state,
      convertedFiles: state.convertedFiles.filter(
        (file) => file.fileId !== fileId,
      ),
    };
  }),
  on(HomeActions.lockFileBox, (state) => ({
    ...state,
    fileBoxLocked: true,
  })),
  on(HomeActions.lockGerber, (state) => ({
    ...state,
    gerberLocked: true,
  })),
  on(HomeActions.lockLectra, (state) => ({
    ...state,
    lectraLocked: true,
  })),
  on(HomeActions.unlockFileBox, (state) => ({
    ...state,
    fileBoxLocked: false,
  })),
  on(HomeActions.unlockGerber, (state) => ({
    ...state,
    gerberLocked: false,
  })),
  on(HomeActions.unlockLectra, (state) => ({
    ...state,
    lectraLocked: false,
  })),
  on(HomeActions.resetAllFiles, (state) => {
    return {
      ...state,
      uploadedFiles: state.uploadedFiles.map((file) => ({
        ...file,
        status: JobStatus.Wait,
      })),
    };
  }),
  on(HomeActions.resetAllGerberFiles, (state) => {
    return {
      ...state,
      gerberFiles: state.gerberFiles.map((file) => ({
        ...file,
        status: JobStatus.Wait,
      })),
    };
  }),
  on(HomeActions.resetAllLectraFiles, (state) => {
    return {
      ...state,
      lectraFiles: state.lectraFiles.map((file) => ({
        ...file,
        status: JobStatus.Wait,
      })),
    };
  }),
  on(HomeActions.loadMonthlyConvertedFilesSuccess, (state, { files }) => ({
    ...state,
    monthlyConvertedFiles: files,
  })),
);
